import { useState } from 'react'
import {
  useDistributionModes,
  useUpdateDistributionMode,
  useToggleDistributionMode,
} from '@/hooks/useDistributionModes'
import { usePlans } from '@/hooks/usePlans'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { StatusBadge } from '@/components/common/StatusBadge'
import { Skeleton } from '@/components/ui/skeleton'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { DistributionMode, DistributionModeUpdateRequest } from '@/types'
import { Pencil, Shuffle } from 'lucide-react'
import { toast } from 'sonner'
import { getApiErrorMessage } from '@/lib/utils'

const PLATFORM_COLORS: Record<string, string> = {
  tiktok: 'bg-pink-500',
  instagram: 'bg-gradient-to-r from-purple-500 to-orange-400',
  google_news: 'bg-blue-500',
  twitter: 'bg-sky-500',
  facebook: 'bg-blue-600',
}

export const DistributionModesPage = () => {
  const { data: modes, isLoading } = useDistributionModes()
  const { data: plansData } = usePlans()
  const updateMode = useUpdateDistributionMode()
  const toggleMode = useToggleDistributionMode()

  const planOptions = (plansData ?? []).map((p: { name: string }) => p.name)

  const [editMode, setEditMode] = useState<DistributionMode | null>(null)
  const [editForm, setEditForm] = useState<DistributionModeUpdateRequest>({})

  const openEdit = (mode: DistributionMode) => {
    setEditForm({
      name: mode.name,
      description: mode.description,
      default_weights: { ...mode.default_weights },
      available_for_plans: [...mode.available_for_plans],
    })
    setEditMode(mode)
  }

  const weightsTotal = Object.values(editForm.default_weights ?? {}).reduce((s, v) => s + v, 0)
  const weightsValid =
    Object.keys(editForm.default_weights ?? {}).length === 0 || weightsTotal === 100

  const handleSave = async () => {
    if (!editMode) return
    if (!weightsValid) {
      toast.error(`Los pesos deben sumar 100% (actual: ${weightsTotal}%)`)
      return
    }
    try {
      await updateMode.mutateAsync({ id: editMode.id, updates: editForm })
      setEditMode(null)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al actualizar modo de distribucion'))
    }
  }

  const togglePlan = (plans: string[], plan: string) => {
    return plans.includes(plan) ? plans.filter((p) => p !== plan) : [...plans, plan]
  }

  const updateWeight = (platform: string, value: number) => {
    setEditForm((f) => ({
      ...f,
      default_weights: {
        ...(f.default_weights ?? {}),
        [platform]: value,
      },
    }))
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Modos de Distribucion</h1>
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Modos de Distribucion</h1>

      <div className="grid gap-4 md:grid-cols-2">
        {modes?.map((mode) => (
          <Card key={mode.id} className={!mode.is_active ? 'opacity-60' : ''}>
            <CardHeader className="flex flex-row items-start justify-between pb-2">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shuffle className="h-4 w-4 text-primary" />
                  {mode.name}
                </CardTitle>
                <p className="mt-1 font-mono text-xs text-muted-foreground">{mode.mode_id}</p>
              </div>
              <StatusBadge status={mode.is_active ? 'active' : 'inactive'} />
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {mode.description || 'Sin descripcion'}
              </p>

              {/* Weights as progress bars */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Pesos por defecto</p>
                {Object.entries(mode.default_weights).map(([platform, weight]) => (
                  <div key={platform} className="flex items-center gap-2">
                    <span className="w-24 text-xs capitalize">{platform.replace('_', ' ')}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className={`h-full rounded-full ${PLATFORM_COLORS[platform] || 'bg-primary'}`}
                        style={{ width: `${weight}%` }}
                      />
                    </div>
                    <span className="w-8 text-right text-xs font-medium">{weight}%</span>
                  </div>
                ))}
                {Object.keys(mode.default_weights).length === 0 && (
                  <span className="text-xs text-muted-foreground">Sin pesos configurados</span>
                )}
              </div>

              {/* Available plans */}
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Planes</p>
                <div className="flex flex-wrap gap-1">
                  {mode.available_for_plans.map((plan) => (
                    <Badge key={plan} variant="default" className="text-xs capitalize">
                      {plan}
                    </Badge>
                  ))}
                  {mode.available_for_plans.length === 0 && (
                    <span className="text-xs text-muted-foreground">Ninguno</span>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 border-t pt-3">
                <Switch
                  checked={mode.is_active}
                  onCheckedChange={() => toggleMode.mutate(mode.id)}
                />
                <Button variant="ghost" size="sm" onClick={() => openEdit(mode)}>
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {modes && modes.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">
          No hay modos de distribucion configurados
        </div>
      )}

      {/* Edit Distribution Mode Dialog */}
      <Dialog open={!!editMode} onOpenChange={(open) => !open && setEditMode(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar modo: {editMode?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Nombre</Label>
              <Input
                value={editForm.name ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Descripcion</Label>
              <Input
                value={editForm.description ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>

            {/* Weight sliders */}
            <div className="space-y-3 rounded-lg border p-3">
              <Label className="text-sm font-medium">Pesos por plataforma</Label>
              {Object.entries(editForm.default_weights ?? {}).map(([platform, weight]) => (
                <div key={platform} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs capitalize">{platform.replace('_', ' ')}</Label>
                    <span className="text-xs font-medium">{weight}%</span>
                  </div>
                  <Input
                    type="range"
                    min={0}
                    max={100}
                    value={weight}
                    onChange={(e) => updateWeight(platform, Number(e.target.value))}
                    className="h-2 cursor-pointer"
                  />
                </div>
              ))}
              {Object.keys(editForm.default_weights ?? {}).length === 0 && (
                <p className="text-xs text-muted-foreground">Sin plataformas configuradas</p>
              )}
              {Object.keys(editForm.default_weights ?? {}).length > 0 && (
                <div
                  className={`rounded px-2 py-1.5 text-sm font-medium ${weightsValid ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}
                >
                  Total: {weightsTotal}% {weightsValid ? '' : '(debe ser 100%)'}
                </div>
              )}
              {/* Add new platform weight */}
              <div className="border-t pt-3">
                <AddPlatformWeight
                  existingPlatforms={Object.keys(editForm.default_weights ?? {})}
                  onAdd={(platform) => updateWeight(platform, 20)}
                />
              </div>
            </div>

            {/* Plan checkboxes */}
            <div className="space-y-2">
              <Label>Disponible para planes</Label>
              <div className="flex flex-wrap gap-3">
                {planOptions.map((plan) => (
                  <label key={plan} className="flex items-center gap-2 text-sm capitalize">
                    <Checkbox
                      checked={(editForm.available_for_plans ?? []).includes(plan)}
                      onCheckedChange={() =>
                        setEditForm((f) => ({
                          ...f,
                          available_for_plans: togglePlan(f.available_for_plans ?? [], plan),
                        }))
                      }
                    />
                    {plan}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMode(null)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={updateMode.isPending || !weightsValid}>
              {updateMode.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function AddPlatformWeight({
  existingPlatforms,
  onAdd,
}: {
  existingPlatforms: string[]
  onAdd: (platform: string) => void
}) {
  const [newPlatform, setNewPlatform] = useState('')

  const handleAdd = () => {
    const trimmed = newPlatform.trim().toLowerCase().replace(/\s+/g, '_')
    if (trimmed && !existingPlatforms.includes(trimmed)) {
      onAdd(trimmed)
      setNewPlatform('')
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        placeholder="Nueva plataforma..."
        value={newPlatform}
        onChange={(e) => setNewPlatform(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        className="flex-1"
      />
      <Button variant="outline" size="sm" onClick={handleAdd} disabled={!newPlatform.trim()}>
        Agregar
      </Button>
    </div>
  )
}
